import * as React from "react";
import { useMultipleHangars } from "../hooks/useMultipleHangars";
import { Hangar } from "../types";
import { HangarSkeleton } from "../screens/Hangar/HangarSkeleton";
import { NotFoundCard } from "../components/NotFoundCard";

type Props = {
  hangar_id: string;
  isReference: boolean;
};

const HangarStateContainerState = React.createContext<{
  hangar: Hangar;
  getHangar: (hangar_id: string) => Hangar;
  setHangar: React.Dispatch<React.SetStateAction<Hangar>>;
  history: any;
  isReference: boolean;
} | undefined>(undefined);

export const HangarStateContainer: React.FC<{
  children?: React.ReactNode;
  initialState: Props;
}> = ({ children, initialState }) => {
  const {
    ready,
    hangars,
    getHangar,
    setHangar,
    history,
  } = useMultipleHangars([initialState.hangar_id], initialState.isReference);
  // Find the specific requested hangar (not just first in array, which could be the holding area)
  const hangar = getHangar(initialState.hangar_id);
  const hangarNotFound = ready && !hangar;

  return (
    <HangarStateContainerState.Provider
      value={{
        hangar,
        getHangar,
        setHangar,
        history,
        isReference: initialState.isReference,
      }}
    >
      {ready ? (
        hangarNotFound ? (
          <NotFoundCard type="hangar" />
        ) : (
          children
        )
      ) : (
        <HangarSkeleton />
      )}
    </HangarStateContainerState.Provider>
  );
};

/**
 * Hook into the current identity context.
 */
export const useHangarState = () => {
  const context = React.useContext(HangarStateContainerState);
  if (!context) {
    throw new Error("useHangarState must be used within a HangarStateContainer");
  }
  return context;
};

/**
 * Optional hook that returns undefined if not within a HangarStateContainer.
 * Use this when the component might be rendered in both Hangar and non-Hangar contexts.
 */
export const useOptionalHangarState = () => {
  const context = React.useContext(HangarStateContainerState);
  return context;
};
